/**
 * AIInterviewSetup - Setup screen for AI-conducted interviews
 * Research goal, interview type, person name, focus topics
 */

import { useState } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  User,
  Briefcase,
  Users,
  Compass,
  CheckCircle,
  Target,
  Plus,
  X,
  ArrowRight,
  Sparkles,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { AIInterviewType } from '@ispot/shared'

// ============================================================================
// INTERVIEW TYPE CONFIGS
// ============================================================================

interface InterviewTypeOption {
  id: AIInterviewType
  label: string
  description: string
  icon: React.ReactNode
}

const INTERVIEW_TYPES: InterviewTypeOption[] = [
  { id: 'user_interview', label: 'User Interview', description: 'End-user conversation', icon: <User className="w-4 h-4" /> },
  { id: 'buyer_interview', label: 'Buyer Interview', description: 'Decision-maker conversation', icon: <Briefcase className="w-4 h-4" /> },
  { id: 'customer_interview', label: 'Customer Interview', description: 'General customer', icon: <Users className="w-4 h-4" /> },
  { id: 'stakeholder_interview', label: 'Stakeholder', description: 'Internal stakeholder', icon: <Users className="w-4 h-4" /> },
  { id: 'discovery_meeting', label: 'Discovery Meeting', description: 'Explore problem space', icon: <Compass className="w-4 h-4" /> },
  { id: 'validation_meeting', label: 'Validation Meeting', description: 'Validate assumptions', icon: <CheckCircle className="w-4 h-4" /> },
]

// ============================================================================
// COMPONENT
// ============================================================================

interface AIInterviewSetupProps {
  onStart: (data: {
    researchGoal: string
    interviewType: AIInterviewType
    linkedPersonName: string
    focusTopics: string[]
    title?: string
  }) => void
  onCancel: () => void
  isLoading?: boolean
  error?: string | null
}

export function AIInterviewSetup({ onStart, onCancel, isLoading, error }: AIInterviewSetupProps) {
  const [researchGoal, setResearchGoal] = useState('')
  const [interviewType, setInterviewType] = useState<AIInterviewType>('user_interview')
  const [personName, setPersonName] = useState('')
  const [focusTopics, setFocusTopics] = useState<string[]>([])
  const [topicInput, setTopicInput] = useState('')

  const canStart = researchGoal.trim().length > 0 && personName.trim().length > 0

  const addTopic = () => {
    const topic = topicInput.trim()
    if (topic && !focusTopics.includes(topic)) {
      setFocusTopics([...focusTopics, topic])
      setTopicInput('')
    }
  }

  const removeTopic = (topic: string) => {
    setFocusTopics(focusTopics.filter(t => t !== topic))
  }

  const handleSubmit = () => {
    if (!canStart) return
    onStart({
      researchGoal: researchGoal.trim(),
      interviewType,
      linkedPersonName: personName.trim(),
      focusTopics,
    })
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-6">
      <div className="space-y-2">
        <div className="inline-flex items-center gap-2 rounded-md bg-accent-50 px-3 py-1.5 text-caption font-semibold text-accent-700">
          <Sparkles className="w-4 h-4" />
          AI Interviewer
        </div>
        <h2 className="font-display text-heading font-semibold text-fg">
          Set up your interview
        </h2>
        <p className="max-w-xl text-body text-fg-secondary">
          AI will conduct an adaptive interview based on your research goal
        </p>
      </div>

      {/* Research Goal */}
      <Card className="space-y-2 rounded-md border border-border bg-bg-card p-4 shadow-none">
        <label className="flex items-center gap-2 text-body font-medium text-fg">
          <Target className="w-4 h-4" />
          Research goal
        </label>
        <textarea
          value={researchGoal}
          onChange={e => setResearchGoal(e.target.value)}
          placeholder="What do you want to learn? e.g. 'Understand how product managers prioritize features and what tools they use'"
          className="h-24 w-full resize-none rounded-md border border-border bg-bg-card px-3 py-2 text-body text-fg outline-none placeholder:text-fg-muted focus:border-accent-500 focus:ring-2 focus:ring-accent-500/15"
        />
      </Card>

      {/* Person Name */}
      <Card className="space-y-2 rounded-md border border-border bg-bg-card p-4 shadow-none">
        <label className="flex items-center gap-2 text-body font-medium text-fg">
          <User className="w-4 h-4" />
          Interviewee name
        </label>
        <input
          type="text"
          value={personName}
          onChange={e => setPersonName(e.target.value)}
          placeholder="Name of the person being interviewed"
          className="w-full rounded-md border border-border bg-bg-card px-3 py-2 text-body text-fg outline-none placeholder:text-fg-muted focus:border-accent-500 focus:ring-2 focus:ring-accent-500/15"
        />
        <p className="text-caption text-fg-muted">
          Will be automatically created as a Persona entity
        </p>
      </Card>

      {/* Interview Type */}
      <Card className="space-y-3 rounded-md border border-border bg-bg-card p-4 shadow-none">
        <label className="text-body font-medium text-fg">Interview type</label>
        <div className="grid grid-cols-2 gap-2 max-sm:grid-cols-1">
          {INTERVIEW_TYPES.map(type => (
            <button
              key={type.id}
              onClick={() => setInterviewType(type.id)}
              className={cn(
                'flex min-h-[56px] items-center gap-2 rounded-md border p-2.5 text-left text-body transition-colors',
                interviewType === type.id
                  ? 'border-accent-500 bg-accent-50 text-fg'
                  : 'border-border text-fg-secondary hover:border-border-strong hover:bg-bg-secondary',
              )}
            >
              {type.icon}
              <div>
                <div className="text-caption font-semibold">{type.label}</div>
                <div className="text-[11px] text-fg-muted">{type.description}</div>
              </div>
            </button>
          ))}
        </div>
      </Card>

      {/* Focus Topics */}
      <Card className="space-y-3 rounded-md border border-border bg-bg-card p-4 shadow-none">
        <label className="text-body font-medium text-fg">Focus topics <span className="font-normal text-fg-muted">(optional)</span></label>
        <div className="flex gap-2">
          <input
            type="text"
            value={topicInput}
            onChange={e => setTopicInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTopic())}
            placeholder="Add a topic to focus on..."
            className="flex-1 rounded-md border border-border bg-bg-card px-3 py-2 text-body text-fg outline-none placeholder:text-fg-muted focus:border-accent-500 focus:ring-2 focus:ring-accent-500/15"
          />
          <Button size="sm" variant="outline" onClick={addTopic} disabled={!topicInput.trim()} className="border-border">
            <Plus className="w-3.5 h-3.5" />
          </Button>
        </div>
        {focusTopics.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {focusTopics.map(topic => (
              <Badge key={topic} variant="secondary" className="gap-1">
                {topic}
                <button onClick={() => removeTopic(topic)} className="hover:text-danger-fg">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </Card>

      {/* Actions */}
      <div className="flex justify-between pt-2">
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          disabled={!canStart || isLoading}
          className="gap-2 bg-accent-500 text-bg-card hover:bg-accent-600"
        >
          {isLoading ? 'Creating...' : 'Generate Interview Plan'}
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  )
}
